import { useMemo, useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";

type Step = "capture" | "submitting" | "done";

const TILES = [
  {
    id: "customer-experience",
    label: "Customer experience",
    hint: "How customers discover, buy, get served or stay with you.",
  },
  {
    id: "how-work-gets-done",
    label: "How work gets done",
    hint: "Roles, workflows, agents, systems and decisions.",
  },
  {
    id: "new-proposition",
    label: "A new proposition",
    hint: "Something AI makes possible that wasn’t before.",
  },
  {
    id: "the-business-itself",
    label: "The business itself",
    hint: "We know something needs to change. We’re still figuring out what.",
  },
] as const;

const emailSchema = z.string().trim().email().max(255);
const nameSchema = z.string().trim().max(120);
const contextSchema = z.string().trim().max(1600);

const selectableCls = (active: boolean) =>
  [
    "text-left transition-colors duration-200 border",
    active
      ? "border-[hsl(var(--accent-brick))] bg-[hsl(var(--bg-canvas))] text-[hsl(var(--fg-cream))]"
      : "border-[hsl(var(--border-hairline))] text-[hsl(var(--fg-muted))] hover:text-[hsl(var(--fg-cream))] hover:border-[hsl(var(--fg-muted))]",
  ].join(" ");

interface LeadCaptureFlowProps {
  /**
   * "page" renders the standalone panel (border + panel background + headline).
   * "modal" drops the chrome and headline — the dialog supplies both.
   */
  variant?: "page" | "modal";
}

export function LeadCaptureFlow({ variant = "page" }: LeadCaptureFlowProps) {
  const isModal = variant === "modal";

  const [step, setStep] = useState<Step>("capture");
  const [topic, setTopic] = useState<string | null>(null);
  const [context, setContext] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const emailValid = useMemo(() => emailSchema.safeParse(email).success, [email]);
  const canSubmit = Boolean(topic) && emailValid;
  const selectedTopic = TILES.find((tile) => tile.id === topic);

  const handleCapture = async () => {
    setTouched(true);
    if (!canSubmit) return;

    setSubmitError(null);
    setStep("submitting");

    try {
      const description = [
        selectedTopic?.label ?? "Unspecified",
        contextSchema.parse(context) || "No additional context provided.",
      ].join("\n\n");

      const { error } = await supabase.functions.invoke("submit-starter-request", {
        body: {
          taskType: topic,
          workflow: { description },
          contact: {
            name: nameSchema.parse(name),
            workEmail: emailSchema.parse(email),
            consent: true,
          },
          qualificationStatus: "deferred",
        },
      });

      if (error) throw error;
      setStep("done");
    } catch (err) {
      console.error("submit-starter-request failed", err);
      setSubmitError("Something broke on our end. Try again in a moment.");
      setStep("capture");
      toast.error("Submission failed. Try again.");
    }
  };

  const wrapperCls = isModal
    ? "w-full"
    : "w-full max-w-[760px] mx-auto border border-[hsl(var(--border-hairline))] bg-[hsl(var(--panel))] p-6 md:p-8";

  const headingCls = isModal ? "text-[20px] font-medium" : "display-md";

  return (
    <div className={wrapperCls} style={isModal ? undefined : { borderRadius: 2 }}>
      {(step === "capture" || step === "submitting") && (
        <div key="capture" className="motion-safe:animate-fade-in">
          {!isModal && (
            <>
              <h2 className="display-md text-[hsl(var(--fg-cream))]">Talk to Brick.</h2>
              <p className="mt-3 text-[hsl(var(--fg-muted))]">
                Tell us what you want to change, rethink or build. We’ll take it from there.
              </p>
            </>
          )}

          <fieldset className={isModal ? "" : "mt-6"}>
            <legend className="eyebrow text-[hsl(var(--fg-muted))]">What are you rethinking?</legend>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              {TILES.map((tile) => {
                const active = topic === tile.id;
                return (
                  <button
                    key={tile.id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setTopic(tile.id)}
                    className={selectableCls(active) + " p-4"}
                  >
                    <span className="flex items-center gap-2 text-[15px] font-medium">
                      <span
                        aria-hidden
                        className={
                          "h-1.5 w-1.5 rounded-full " +
                          (active
                            ? "bg-[hsl(var(--accent-brick))]"
                            : "bg-[hsl(var(--border-hairline))]")
                        }
                      />
                      {tile.label}
                    </span>
                    <span className="mt-2 block text-xs leading-relaxed text-[hsl(var(--fg-muted))]">
                      {tile.hint}
                    </span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div className="mt-6">
            <Label htmlFor="lead-context" className="text-[13px] text-[hsl(var(--fg-cream))]/80">
              Tell us a little more
            </Label>
            <Textarea
              id="lead-context"
              rows={4}
              maxLength={1600}
              value={context}
              onChange={(event) => setContext(event.target.value)}
              placeholder="What are you seeing? What feels outdated, difficult or newly possible?"
              className="mt-2 resize-y bg-[hsl(var(--bg-canvas))] border-[hsl(var(--border-hairline))] text-[hsl(var(--fg-cream))] rounded-none"
            />
          </div>

          <div className="mt-6 grid gap-3 md:grid-cols-2">
            <div>
              <Label htmlFor="lead-email" className="text-[13px] text-[hsl(var(--fg-cream))]/80">
                Work email
              </Label>
              <div className="relative mt-2">
                <Input
                  id="lead-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@company.com"
                  className="bg-[hsl(var(--bg-canvas))] border-[hsl(var(--border-hairline))] text-[hsl(var(--fg-cream))] rounded-none pr-9"
                />
                {emailValid && (
                  <Check
                    aria-hidden
                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--accent-brick))] motion-safe:animate-fade-in"
                  />
                )}
              </div>
              {touched && !emailValid && (
                <p className="mt-1 text-xs text-[hsl(var(--accent-brick))]">Enter a valid work email.</p>
              )}
            </div>
            <div>
              <Label htmlFor="lead-name" className="text-[13px] text-[hsl(var(--fg-cream))]/80">
                First name <span className="text-[hsl(var(--fg-muted))]">(optional)</span>
              </Label>
              <Input
                id="lead-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Erik"
                className="mt-2 bg-[hsl(var(--bg-canvas))] border-[hsl(var(--border-hairline))] text-[hsl(var(--fg-cream))] rounded-none"
              />
            </div>
          </div>

          {touched && !topic && (
            <p className="mt-3 text-xs text-[hsl(var(--accent-brick))]">Choose the closest starting point.</p>
          )}
          {submitError && <p className="mt-3 text-xs text-[hsl(var(--accent-brick))]">{submitError}</p>}

          <button
            type="button"
            onClick={handleCapture}
            disabled={step === "submitting"}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-[hsl(var(--accent-brick))] px-6 py-3.5 text-sm font-medium text-[hsl(var(--fg-cream))] transition-colors hover:bg-[hsl(var(--accent-brick-hover))] disabled:opacity-40"
          >
            {step === "submitting" ? (
              "Sending…"
            ) : (
              <>
                Talk to Brick <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
          <p className="mt-3 text-center text-xs text-[hsl(var(--fg-muted))]">
            No pitch deck. We’ll reply within one working day.
          </p>
        </div>
      )}

      {step === "done" && (
        <div key="done" className="motion-safe:animate-fade-in">
          <p className="eyebrow text-[hsl(var(--fg-muted))]">Received</p>
          <h2 className={headingCls + " mt-2 text-[hsl(var(--fg-cream))]"}>Thanks — we’ve got it.</h2>
          <p className="mt-3 text-[hsl(var(--fg-muted))]">
            We’ll read what you sent and reply within one working day.
          </p>
        </div>
      )}
    </div>
  );
}

export default LeadCaptureFlow;
